// src/pages/SupportPage.jsx
import React, { useState } from 'react';
import { Mail } from 'lucide-react';

const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:cyborguser2020@gmail.com?subject=${encodeURIComponent(
      formData.subject
    )}&body=${encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    )}`;
    window.location.href = mailtoLink;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const faqs = [
    {
      question: 'How do I scan a barcode?',
      answer: 'Open the app, tap the Scan tab, and point your camera at a product barcode. The app will automatically detect and scan it.',
    },
    {
      question: 'How do I search for a product?',
      answer: 'Tap the Search tab and type a product name (e.g., "Milk", "Nutella"). Results are pulled from the Open Food Facts database with millions of products.',
    },
    {
      question: 'How does the nutrition tracker work?',
      answer: 'After scanning or searching for a product, tap "Log" to add it to your daily tracker. The Tracker tab shows your daily totals for calories, protein, carbs, and fat with visual progress bars.',
    },
    {
      question: 'What is Nutri-Score?',
      answer: 'Nutri-Score is a nutrition label system that grades food from A (healthiest) to E (least healthy). It appears on product details when available in the database.',
    },
    {
      question: 'How do meal reminders work?',
      answer: 'Go to Profile > Settings and enable "Meal Reminders". You\'ll receive daily notifications at breakfast (8 AM), lunch (12:30 PM), water (3 PM), and dinner (7 PM) in your local time.',
    },
    {
      question: 'Why is a product not found?',
      answer: 'Not all products are in the Open Food Facts database. You can contribute by adding missing products to their database at world.openfoodfacts.org.',
    },
    {
      question: 'How do I save favorite products?',
      answer: 'When viewing a product, tap the heart icon to add it to your favorites. Access them anytime from the Favorites tab.',
    },
    {
      question: 'Is my data private?',
      answer: 'Yes! All your scan history, favorites, and nutrition logs are stored locally on your device. We do not upload or share your personal data. No account is required.',
    },
    {
      question: 'How do I enable dark mode?',
      answer: 'Go to Profile > Settings and toggle the Dark Mode switch to change the app theme.',
    },
    {
      question: 'Can I remove ads?',
      answer: 'Currently, ads help us keep the app free. You can support us with an optional donation in the Profile > Settings screen.',
    },
    {
      question: 'How accurate is the nutritional information?',
      answer: 'The nutritional information comes from Open Food Facts, a collaborative database. While generally accurate, we recommend verifying critical information with product packaging, especially for allergies.',
    },
    {
      question: 'What should I do if the barcode won\'t scan?',
      answer: 'Ensure good lighting, hold your phone steady, and make sure the barcode is clearly visible. Try cleaning your camera lens if issues persist. You can also search for the product by name.',
    },
    {
      question: 'Can I use the app offline?',
      answer: 'You need an internet connection to scan barcodes and search for products. However, your scan history, favorites, and nutrition tracker data are available offline.',
    },
    {
      question: 'How do I clear my scan history?',
      answer: 'Go to the History tab, tap the delete icon in the top right, and confirm to clear all history. You can also swipe individual items to delete them.',
    },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-12">
      {/* Header */}
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg text-center">
        <Mail className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Support Center</h1>
        <p className="text-lg text-gray-600">
          We're here to help! Find answers to common questions or get in touch with us.
        </p>
      </div>

      {/* FAQs */}
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-gray-200 pb-6 last:border-0">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {faq.question}
              </h3>
              <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Form */}
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h2>
        <p className="text-gray-600 mb-8">
          Can't find what you're looking for? Send us a message and we'll get back to you as soon as possible.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="your.email@example.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <select
              id="subject"
              name="subject"
              required
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Select a subject</option>
              <option value="Technical Issue">Technical Issue</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Product Information">Product Information</option>
              <option value="Nutrition Tracking">Nutrition Tracking</option>
              <option value="Notifications">Notifications</option>
              <option value="Account & Data">Account & Data</option>
              <option value="Billing & Donations">Billing & Donations</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              required
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              placeholder="Describe your issue or question in detail..."
            />
          </div>

          <button
            type="submit"
            className="w-full md:w-auto px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
          >
            <Mail className="w-5 h-5" />
            <span>Send Message</span>
          </button>
        </form>

        <div className="mt-8 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <strong>Direct Email:</strong>{' '}
            <a href="mailto:cyborguser2020@gmail.com" className="text-green-600 hover:underline">
              cyborguser2020@gmail.com
            </a>
          </p>
          <p className="text-sm text-gray-600 mt-2">
            We typically respond within 24-48 hours during business days.
          </p>
        </div>
      </div>

      {/* Troubleshooting Guide */}
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Troubleshooting Guide</h2>

        <div className="space-y-8">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center mr-3 text-sm font-bold">1</span>
              Camera Not Working
            </h3>
            <ul className="list-disc pl-12 space-y-2 text-gray-700">
              <li>Check if you've granted camera permissions in your device settings</li>
              <li>Restart the app and try again</li>
              <li>Ensure no other app is using the camera</li>
              <li>Clear the app cache in settings</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mr-3 text-sm font-bold">2</span>
              App Crashes or Freezes
            </h3>
            <ul className="list-disc pl-12 space-y-2 text-gray-700">
              <li>Update to the latest version of the app</li>
              <li>Restart your device</li>
              <li>Clear app data (note: this will delete your history and favorites)</li>
              <li>Reinstall the app if issues persist</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center mr-3 text-sm font-bold">3</span>
              Product Information Missing or Incorrect
            </h3>
            <ul className="list-disc pl-12 space-y-2 text-gray-700">
              <li>Try scanning the barcode again in better lighting</li>
              <li>Try searching for the product by name in the Search tab</li>
              <li>Check if the product exists in Open Food Facts database</li>
              <li>You can contribute by adding the product to Open Food Facts</li>
              <li>Verify information with product packaging when critical</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-8 h-8 bg-red-100 text-red-700 rounded-full flex items-center justify-center mr-3 text-sm font-bold">4</span>
              Connection Issues
            </h3>
            <ul className="list-disc pl-12 space-y-2 text-gray-700">
              <li>Check your internet connection</li>
              <li>Switch between WiFi and mobile data</li>
              <li>Disable VPN if you're using one</li>
              <li>Wait a moment and try again — servers may be temporarily busy</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-8 h-8 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center mr-3 text-sm font-bold">5</span>
              Notifications Not Working
            </h3>
            <ul className="list-disc pl-12 space-y-2 text-gray-700">
              <li>Check that "Meal Reminders" is enabled in Profile &gt; Settings</li>
              <li>Make sure notifications are allowed in your device settings for NutriLens Pro</li>
              <li>On Android, check that battery optimization is not blocking notifications</li>
              <li>Try disabling and re-enabling reminders in the app</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Additional Resources */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-3xl p-8 md:p-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Additional Resources</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Open Food Facts</h3>
            <p className="text-gray-600 mb-4">
              Learn more about the database powering NutriLens Pro
            </p>
            <a
              href="https://world.openfoodfacts.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:underline font-medium inline-flex items-center"
            >
              Visit Website
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Contribute Data</h3>
            <p className="text-gray-600 mb-4">
              Help improve the database by adding missing products
            </p>
            <a
              href="https://world.openfoodfacts.org/contribute"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:underline font-medium inline-flex items-center"
            >
              Start Contributing
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Community Forum</h3>
            <p className="text-gray-600 mb-4">
              Connect with other users and share tips
            </p>
            <a
              href="https://world.openfoodfacts.org/talk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:underline font-medium inline-flex items-center"
            >
              Join Discussion
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* System Requirements */}
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">System Requirements</h2>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">iOS Devices</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                iOS 12.0 or later
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                iPhone and iPad compatible
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Camera access required for scanning
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Internet connection required for scanning and search
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Android Devices</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Android 6.0 (Marshmallow) or later
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Compatible with most Android devices
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Camera access required for scanning
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Internet connection required for scanning and search
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
